import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { IOption } from '@user/components/Filters'
import { MultiSelectAutoComplete } from '@user/components/Filters/components/MultiSelectFilter/MultiSelectAutoComplete'
import { selectSalespersonFilterOptions } from '@user/store/map'
import { useSalespersonFilterSelection } from '@user/components/HereMap/HereMapNew/hooks/useSalespersonFilterSelection'
import { SalespersonFilterProvider } from './SalespersonFilterProvider'

type Props = {
  open: boolean
  onClose: () => void
}

export function SalespersonFilterModal({ open, onClose }: Props) {
  const options = useSelector(selectSalespersonFilterOptions)
  const { selection, setSelection } = useSalespersonFilterSelection()

  const value = useMemo<IOption[]>(
    () =>
      options
        .filter((o) => selection.includes(o.userId))
        .map((o) => ({ value: o.userId, label: o.name })),
    [options, selection],
  )

  const setValue = useCallback(
    (newValue: IOption[]) => {
      // Rule 2: an empty selection is never a valid state — the autocomplete
      // lets the last chip be deleted, so enforce the same "reset to everyone"
      // invariant the Select All button already does.
      setSelection(
        newValue.length > 0
          ? newValue.map((o) => String(o.value))
          : options.map((o) => o.userId),
      )
    },
    [setSelection, options],
  )

  const handleSelectAll = useCallback(() => {
    setSelection(options.map((o) => o.userId))
  }, [setSelection, options])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="salesperson-filter-title"
      data-testid="salesperson-filter-dropdown"
      sx={{
        '& .MuiDialog-paper': {
          width: '33vw',
          minWidth: 400,
          maxWidth: 600,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography id="salesperson-filter-title" variant="h6">
            Filter by salesperson
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <MultiSelectAutoComplete
          value={value}
          setValue={setValue}
          component={SalespersonFilterProvider}
        />

        <Box
          sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}
        >
          <Button
            data-testid="salesperson-filter-select-all"
            size="small"
            onClick={handleSelectAll}
          >
            Select All
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
